import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TrashIcon,
    PlayIcon,
    PauseIcon,
    CalendarBlankIcon,
    CircleIcon,
    PlusIcon,
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    AnimatedActionBarModeTransition,
    DateTimeRangePicker,
    ActionBar,
    SelectionBar,
    toast,
} from '@/components';
import {
    ActionType,
    ActionVariant,
    DividerOrientation,
    buildFiltersAction,
    buildSortAction,
    buildMultiSelectToggle,
} from '@/components/ActionBar';
import type { ActionConfig } from '@/components/ActionBar';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { usePopoverCloseSignal } from '@/stores';
import { AppRoutes } from '@/constants/routes';
import { revalidateAlertUnitCapacity } from '@/features/SettingsView/hooks/useConfiguration';
import { EngineService } from '@/services/engine';
import { Permission } from '@/types/permissions';
import { FullAlertRule } from '@/types/alert';
import type {
    DateRange,
    StatusFilter,
    SortOption,
} from '../hooks/useAlertRulesFilters';
import { SORT_OPTIONS } from '../hooks/useAlertRulesFilters';

const DATE_PICKER_ALIGN_OFFSET = -128;

interface AlertRulesActionBarProps {
    items: FullAlertRule[];
    totalCount: number;
    selectedIds: Set<string>;
    selectedCount: number;
    isMultiSelectMode: boolean;
    enterMultiSelectMode: () => void;
    exitMultiSelectMode: () => void;
    selectAll: (ids: string[]) => void;
    clearSelection: () => void;
    dateRange: DateRange | null;
    onDateRangeChange: (range: DateRange | null) => void;
    statusFilter: StatusFilter;
    onStatusFilterChange: (status: StatusFilter) => void;
    sortOption: SortOption | null;
    onSortOptionChange: (option: SortOption | null) => void;
    onSearchSubmit: (value: string) => void;
    onRevalidate: () => void;
}

export function AlertRulesActionBar({
    items,
    totalCount,
    selectedIds,
    selectedCount,
    isMultiSelectMode,
    enterMultiSelectMode,
    exitMultiSelectMode,
    selectAll,
    clearSelection,
    dateRange,
    onDateRangeChange,
    statusFilter,
    onStatusFilterChange,
    sortOption,
    onSortOptionChange,
    onSearchSubmit,
    onRevalidate,
}: AlertRulesActionBarProps) {
    const { t } = useTranslation([
        I18nNamespace.ALERT_RULES,
        I18nNamespace.COMMON,
    ]);
    const navigate = useNavigate();
    const [searchDraft, setSearchDraft] = useState('');
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const triggerPopoverClose = usePopoverCloseSignal((state) => state.trigger);

    const hasDateFilter = dateRange !== null;
    const hasStatusFilter = statusFilter !== 'all';
    const hasActiveFilters = hasDateFilter || hasStatusFilter;
    const hasSelection = selectedCount > 0;
    const currentSortId = sortOption
        ? `${sortOption.field}-${sortOption.direction}`
        : null;

    const handleNewAlertRule = () => {
        navigate(AppRoutes.ALERT_RULES_CREATE);
    };

    const handleSelectAll = () => {
        if (selectedCount === items.length && items.length > 0) {
            clearSelection();
        } else {
            selectAll(items.map((item) => item.id));
        }
    };

    const handleBulkOperation = useCallback(
        async <TResult,>(
            operation: (id: string) => Promise<TResult>,
            successKeyMessage: string,
            partialKeyMessage: string,
            errorKeyMessage: string,
            shouldClearSelection = false,
        ): Promise<void> => {
            if (selectedCount === 0 || isBulkProcessing) return;

            setIsBulkProcessing(true);
            try {
                const promises = Array.from(selectedIds).map((id) =>
                    operation(id)
                        .then(() => true)
                        .catch(() => false),
                );

                const results = await Promise.all(promises);
                const successCount = results.filter(
                    (success) => success,
                ).length;
                const failedCount = selectedCount - successCount;

                if (failedCount === 0) {
                    toast.success(
                        t(successKeyMessage, { count: selectedCount }),
                    );
                } else if (successCount > 0) {
                    toast.success(
                        t(partialKeyMessage, {
                            success: successCount,
                            failed: failedCount,
                        }),
                    );
                } else {
                    toast.error(t(errorKeyMessage));
                }

                if (successCount > 0) {
                    await revalidateAlertUnitCapacity();
                }
                if (shouldClearSelection) {
                    clearSelection();
                }
            } catch {
                toast.error(t(errorKeyMessage));
            } finally {
                setIsBulkProcessing(false);
                onRevalidate();
            }
        },
        [
            selectedIds,
            selectedCount,
            isBulkProcessing,
            t,
            clearSelection,
            onRevalidate,
        ],
    );

    const handleDeleteSelected = useCallback(async () => {
        await handleBulkOperation(
            (id: string) => EngineService.deleteAlertRule(id),
            'toast.deleteSelectedSuccess',
            'toast.deleteSelectedPartial',
            'toast.deleteSelectedError',
            true,
        );
    }, [handleBulkOperation]);

    const handleActivateSelected = useCallback(async () => {
        await handleBulkOperation(
            (id: string) => EngineService.updateAlertRule(id, { active: true }),
            'toast.activateSuccess',
            'toast.activatePartial',
            'toast.activateError',
        );
    }, [handleBulkOperation]);

    const handleDeactivateSelected = useCallback(async () => {
        await handleBulkOperation(
            (id: string) =>
                EngineService.updateAlertRule(id, { active: false }),
            'toast.deactivateSuccess',
            'toast.deactivatePartial',
            'toast.deactivateError',
        );
    }, [handleBulkOperation]);

    const browseActions: ActionConfig[] = [
        {
            type: ActionType.SEARCH,
            value: searchDraft,
            onChange: setSearchDraft,
            onSubmit: onSearchSubmit,
        },
        { type: ActionType.DIVIDER, orientation: DividerOrientation.VERTICAL },
        buildFiltersAction(t, {
            isActive: hasActiveFilters,
            onClear: () => {
                onDateRangeChange(null);
                onStatusFilterChange('all');
            },
            items: [
                {
                    icon: CalendarBlankIcon,
                    label: t('filters.createdAt'),
                    isActive: hasDateFilter,
                    alignOffset: DATE_PICKER_ALIGN_OFFSET,
                    children: (
                        <DateTimeRangePicker
                            onApply={(range) => {
                                if (range.startTime && range.endTime) {
                                    onDateRangeChange({
                                        startTime: range.startTime,
                                        endTime: range.endTime,
                                    });
                                }
                            }}
                            onClear={() => onDateRangeChange(null)}
                            onClose={triggerPopoverClose}
                        />
                    ),
                },
                {
                    icon: CircleIcon,
                    label: t('filters.statusAll'),
                    isActive: statusFilter === 'all',
                    onClick: () => onStatusFilterChange('all'),
                },
                {
                    icon: PlayIcon,
                    label: t('filters.statusActive'),
                    isActive: statusFilter === 'active',
                    onClick: () => onStatusFilterChange('active'),
                },
                {
                    icon: PauseIcon,
                    label: t('filters.statusInactive'),
                    isActive: statusFilter === 'inactive',
                    onClick: () => onStatusFilterChange('inactive'),
                },
            ],
        }),
        buildSortAction(
            t,
            SORT_OPTIONS.map((option) => ({
                label: t(option.labelKey),
                isActive: currentSortId === option.id,
                onClick: () =>
                    onSortOptionChange({
                        field: option.field,
                        direction: option.direction,
                    }),
            })),
        ),
        buildMultiSelectToggle(t, enterMultiSelectMode),
        {
            type: ActionType.ITEM,
            icon: PlusIcon,
            label: t('actions.newAlertRule'),
            onClick: handleNewAlertRule,
            permission: Permission.CREATE,
        },
    ];

    const bulkActions: ActionConfig[] = hasSelection
        ? [
              {
                  type: ActionType.ITEM,
                  icon: PlayIcon,
                  label: t('actions.activate'),
                  onClick: handleActivateSelected,
                  disabled: isBulkProcessing,
                  permission: Permission.EDIT,
              },
              {
                  type: ActionType.ITEM,
                  icon: PauseIcon,
                  label: t('actions.deactivate'),
                  onClick: handleDeactivateSelected,
                  disabled: isBulkProcessing,
                  permission: Permission.EDIT,
              },
              {
                  type: ActionType.ITEM,
                  icon: TrashIcon,
                  label: t('actions.delete'),
                  onClick: handleDeleteSelected,
                  disabled: isBulkProcessing,
                  variant: ActionVariant.DANGER,
                  permission: Permission.DELETE,
              },
          ]
        : [];

    return (
        <AnimatedActionBarModeTransition
            isMultiSelectMode={isMultiSelectMode}
            browseSlot={<ActionBar actions={browseActions} />}
            selectionSlot={
                <SelectionBar
                    selectedCount={selectedCount}
                    totalCount={totalCount}
                    entityLabel={t('alert-rules:title')}
                    onSelectAll={handleSelectAll}
                    onExitMultiSelect={exitMultiSelectMode}
                    bulkActions={bulkActions}
                />
            }
        />
    );
}
